import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { MarketConfig } from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { setMarketConfigCallable } from '@/lib/callables';
import { MarketPspFields } from './MarketPspFields';
import { marketPspInitial, marketPspPayload, type MarketPspValue } from './marketPspValue';

type Props = {
  open: boolean;
  country: string | null;
  current: MarketConfig | null;
  onClose: () => void;
};

/**
 * Diálogo que configura `/config/markets/items/{country}` pelo callable
 * `setMarketConfig`: status, PSP ativo × modo, PSPs permitidos por venda,
 * entidade vendedora e motivo do audit.
 *
 * Origem: sem issue — commit 5af121d4c, 2026-05-25 23:10 +0200 (Sprint 35 PR G5); refeito na #756 —
 * 2026-10-01 11:33 +0200 (A7: o owner troca o PSP do país pelo admin).
 *
 * Por quê: até a #756 o seletor escondia o Asaas (`p !== 'asaas'`), não tinha
 * modo nem lista de permitidos, e a entidade era texto livre. Agora os campos
 * de PSP são o `MarketPspFields` compartilhado com a aba Países de `/countries`
 * (`CountryEditDialog`): STRIPE e STRIPE SANDBOX em todo país, ASAAS e ASAAS
 * SANDBOX só no BR (owner, 2026-10-01); fiscal derivado do PSP.
 *
 * Contrato: salvar exige confirmação (`AdminConfirm`) e motivo. O servidor
 * revalida tudo e recusa com `failed-precondition` (`asaas_only_br`,
 * `asaas_live_requires_nfse_config`…), cuja mensagem vira `Alert` dentro do
 * modal principal (#1021; o `AdminConfirm` fecha no erro). A troca vale
 * na próxima cobrança do país; assinaturas existentes ficam no gateway delas.
 *
 * O servidor exige `ADMIN_PERMISSIONS.manageMarketConfig` (ROOT/FINANCE); a
 * leitura da lista é da `MarketsPage` (rules `match /config/markets/items/{country}`).
 *
 * Usado por: `routes/MarketsPage.tsx` (rota `/billing/markets`, menu Sistema →
 * Mercados desde 2026-10-13, #935 item 23), admin web.
 *
 * Dicas: país em sandbox é ENSAIO — só tenants de teste vendem; o aviso na
 * tela existe porque o efeito para o cliente real é "não consegue pagar".
 * Spec: `docs/screens/billing-markets.md`. Testes: `marketPspValue.test.ts` (valor e
 * payload) e `features/countries/CountryEditDialog.test.tsx` (mesmos campos).
 *
 * Histórico:
 *  - 2026-10-01 11:33 +0200 — (#756) PSP × modo, permitidos, entidade em lista.
 *  - 2026-10-10 14:40 +0200 — (#798) rota e permissão; doc do `submit` (#796).
 *  - 2026-10-13 11:23 +0200 — (#958) sem entidade: a PJ sai do PSP.
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function MarketConfigEditDialog({
  open,
  country,
  current,
  onClose,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [saleable, setSaleable] = useState(false);
  const [psp, setPsp] = useState<MarketPspValue>({ choice: '', extraAllowed: [], initial: null });
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !country) return;
    setSaleable(current?.saleable ?? false);
    setPsp(marketPspInitial(country, current));
    setReason('');
    setError(null);
    setConfirmOpen(false);
  }, [open, current, country]);

  if (!country) return null;

  function close(): void {
    setError(null);
    onClose();
  }

  /**
   * Validação local: mercado vendável exige PSP; motivo sempre. A entidade
   * saiu do país (#958): a PJ vem do PSP, e o servidor recusa PSP sem entidade
   * ativa (`psp_without_seller`).
   */
  function validate(): string | null {
    if (saleable && psp.choice === '') return t('billing.markets.errors.paymentRequired');
    if (reason.trim().length === 0) return t('billing.markets.errors.reasonRequired');
    return null;
  }

  /** Valida e, se ok, abre o `AdminConfirm`. */
  function requestConfirm(): void {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setConfirmOpen(true);
  }

  /**
   * Grava o mercado via `setMarketConfig` (depois do `AdminConfirm`).
   *
   * Origem: sem issue — commit 5af121d4c, 2026-05-25 23:10 +0200 (Sprint 35, PR G5).
   *
   * Contrato: vendável → envia `marketPspPayload(psp)` (PSP, modo, permitidos,
   * fiscal); bloqueado → `payment_provider: null` (o servidor zera de qualquer
   * jeito). Sem entidade desde a #958: a PJ sai do PSP. Resposta
   * sem `payment_mode` = servidor antigo (janela de deploy, #756) → toast de
   * aviso. Erro → `err.message` (texto do servidor, pt-BR).
   *
   * Dados — input (exemplo):
   *   { "country": "BR", "saleable": true, "payment_provider": "asaas",
   *     "payment_mode": "sandbox", "allowed_payment_providers": ["asaas"],
   *     "fiscal_provider": "…", "reason": "…" }
   *
   * Usado por: `AdminConfirm` deste diálogo.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   *  - 2026-10-13 11:23 +0200 — (#958) sem `seller_entity_id`.
   */
  async function submit(): Promise<void> {
    if (!country) return;
    setSubmitting(true);
    setError(null);
    try {
      const saved = await setMarketConfigCallable({
        country,
        saleable,
        ...(saleable ? marketPspPayload(psp) : { payment_provider: null }),
        reason: reason.trim(),
      });
      toast.success(t('billing.markets.success'));
      // #756: servidor sem `payment_mode` na resposta é o antigo (janela do
      // deploy) — o modo pode ter sido gravado como live.
      if (saleable && saved.payment_mode === undefined) toast.error(t('billing.markets.staleServer'));
      setConfirmOpen(false);
      close();
    } catch (err) {
      setConfirmOpen(false);
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        visible={open && !confirmOpen}
        // #1021 (2ª auditoria 2026-10-25 15:52 +0100): com a ação em voo, Esc/X/clique fora
        // fechavam o modal e a falha caía no `Alert` dele, já fechado — ninguém a via.
        onDismiss={(): void => { if (!(submitting)) { close(); } }}
        header={t('billing.markets.editTitle', { country })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={close} disabled={submitting}>
                {t('tenantDetail.actions.cancel')}
              </Button>
              <Button variant="primary" onClick={requestConfirm} disabled={submitting}>
                {t('billing.markets.save')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label={t('billing.markets.statusLabel')}>
            <RadioGroup
              value={saleable ? 'open' : 'blocked'}
              onChange={({ detail }): void => {
                setSaleable(detail.value === 'open');
              }}
              items={[
                { value: 'open', label: t('billing.markets.statusOpen') },
                { value: 'blocked', label: t('billing.markets.statusBlocked') },
              ]}
            />
          </FormField>

          {saleable && <MarketPspFields country={country} value={psp} onChange={setPsp} />}

          {!saleable && <Alert type="info">{t('billing.markets.blockedNote')}</Alert>}

          <FormField label={t('billing.markets.reason')}>
            <Input
              value={reason}
              onChange={({ detail }): void => {
                setReason(detail.value);
              }}
              placeholder={t('billing.markets.reasonPlaceholder')}
            />
          </FormField>
          {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
              do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
          {error !== null && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </Modal>

      <AdminConfirm
        visible={confirmOpen}
        title={t('billing.markets.confirmTitle')}
        confirmLabel={t('billing.markets.confirmApply')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={submitting}
        onConfirm={(): void => {
          void submit();
        }}
        onDismiss={(): void => {
          setConfirmOpen(false);
        }}
      >
        <Box variant="p">{t('billing.markets.confirmDescription', { country })}</Box>
      </AdminConfirm>
    </>
  );
}
